import React, { useState } from "react";
import { Box, Fade, Link, Paper, Tab, Tabs, Typography } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { ChevronRight } from "lucide-react";

import { TExperience } from "@utils/experience";
import { useLayout } from "@/hooks/useLayout";

// Animation keyframes
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Styled components
const AnimatedTitle = styled(Typography)({
  animation: `${fadeUp} 0.5s ease-out forwards`,
  opacity: 0,
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  position: "relative",
  animation: `${fadeIn} 0.5s ease-out forwards`,
  opacity: 0,
  "& .MuiTabs-indicator": {
    right: 0,
    width: 2,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "& .MuiTabs-flexContainer": {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      right: 0,
      top: 0,
      width: "2px",
      height: "100%",
      background: theme.palette.divider,
    },
  },
  "& .MuiTabs-scroller": {
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#c1c1c1",
      borderRadius: "3px",
      "&:hover": {
        background: "#a8a8a8",
      },
    },
  },
  "& .MuiTab-root": {
    alignItems: "flex-start",
    textAlign: "left",
    paddingLeft: "16px",
    textTransform: "none",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#536493",
      backgroundColor: "rgba(37, 99, 235, 0.04)",
    },
    "&.Mui-selected": {
      color: "#536493",
      backgroundColor: "rgba(37, 99, 235, 0.08)",
    },
  },
  [theme.breakpoints.down("md")]: {
    maxWidth: "none",
    "& .MuiTabs-flexContainer::before": {
      display: "none",
    },
    borderBottom: `2px solid ${theme.palette.divider}`,
    "& .MuiTabs-indicator": {
      left: "unset",
      bottom: 0,
      width: "100%",
      height: 2,
    },
  },
}));

const BadgeItem = styled(Box)(() => ({
  fontSize: "11px",
  padding: "6px 12px",
  borderRadius: "6px",
  display: "flex",
  gap: "8px",
  fontWeight: 600,
  letterSpacing: "0.02em",
  transition: "all 0.2s ease",
  backgroundColor: "#C9DABF",
  color: "#5F6F65",
  animation: `${fadeIn} 0.5s ease-out forwards`,
  opacity: 0,
  "&:hover": {
    transform: "translateY(-1px)",
    backgroundColor: "#fde68a",
  },
}));

const AnimatedListItem = styled(Box)`
  animation: ${fadeUp} 0.5s ease forwards;
  opacity: 0;
`;

const AnimatedContent = styled(Box)`
  animation: ${slideIn} 0.3s ease forwards;
  padding-top: 0 !important;
`;

const HighlightBox = styled(Paper)(({ theme }) => ({
  animation: `${fadeIn} 0.5s ease-out forwards`,
  opacity: 0,
  background: "linear-gradient(to right, rgba(37, 99, 235, 0.04), transparent)",
  padding: "12px 16px",
  marginBottom: "20px",
  borderRadius: "8px",
  transition: "all 0.2s ease",
  "&:hover": {
    background:
      "linear-gradient(to right, rgba(37, 99, 235, 0.08), transparent)",
  },
}));

// TabPanel component with animation
const TabPanel = ({ children, value, index, ...props }: any) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`vertical-tabpanel-${index}`}
    {...props}
  >
    {value === index && (
      <Fade in timeout={300}>
        {children}
      </Fade>
    )}
  </div>
);

type TExperienceProps = {
  experiences: TExperience[];
};

const Experience: React.FC<TExperienceProps> = ({ experiences }) => {
  const [value, setValue] = useState(0);
  const { isMobile } = useLayout();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box
      id="Experience"
      sx={{
        maxWidth: "1100px",
        margin: "0 auto",
        height: { xs: "600px", md: "auto" },
        minHeight: { xs: "auto", md: "400px" },
        py: 8,
        px: 3,
      }}
    >
      <Box justifyContent={"start"} alignItems={"start"} display={"flex"}>
        <AnimatedTitle
          variant="h4"
          sx={{
            mb: 6,
            color: "text.primary",
            fontWeight: 600,
            position: "relative",
            display: "inline-block",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: "-4px",
              left: 0,
              width: "100%",
              height: "2px",
              background: "linear-gradient(to right, #536493, transparent)",
            },
          }}
        >
          Professional Experience
        </AnimatedTitle>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: "400px",
          gap: { xs: 2, md: 4 },
          borderRadius: 2,
        }}
      >
        <StyledTabs
          orientation={isMobile ? "horizontal" : "vertical"}
          variant="scrollable"
          value={value}
          onChange={handleChange}
          sx={{
            minHeight: { xs: "auto", md: "400px" },
          }}
          TabIndicatorProps={{
            style: {
              backgroundColor: "#536493",
              boxShadow: "0 0 8px rgba(37, 99, 235, 0.2)",
            },
          }}
        >
          {experiences.map((exp, index) => (
            <Tab
              key={index}
              label={`${exp.company} - ${exp.title}`}
              id={`vertical-tab-${index}`}
              aria-controls={`vertical-tabpanel-${index}`}
            />
          ))}
        </StyledTabs>

        <Box sx={{ flex: 1 }} textAlign={"start"}>
          {experiences.map((exp, index) => (
            <TabPanel
              key={index}
              value={value}
              index={index}
              style={{
                height: isMobile ? "500px" : "400px",
                overflowY: "auto",
              }}
            >
              <AnimatedContent sx={{ py: 1 }}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    paddingTop: "0px",
                    animation: `${fadeIn} 0.5s ease-out forwards`,
                    opacity: 0,
                  }}
                >
                  {exp.title}{" "}
                  <Link
                    href={exp.link}
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: "#536493",
                      textDecoration: "none",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        textDecoration: "none",
                        color: "#1d4ed8",
                      },
                    }}
                  >
                    @{exp.company}
                  </Link>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    animation: `${fadeIn} 0.5s ease-out forwards`,
                    opacity: 0,
                  }}
                >
                  {exp.range}
                </Typography>

                {exp.badges && exp.badges.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexWrap: "wrap",
                      pt: 2,
                      pb: 2,
                    }}
                  >
                    {exp.badges.map((badge, idx) => (
                      <BadgeItem
                        key={idx}
                        sx={{
                          animationDelay: `${idx * 0.1}s`,
                        }}
                      >
                        {badge.icon}
                        {badge.text}
                      </BadgeItem>
                    ))}
                  </Box>
                )}

                {exp.description && (
                  <HighlightBox elevation={0}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.6,
                        fontWeight: 500,
                      }}
                    >
                      {exp.description}
                    </Typography>
                  </HighlightBox>
                )}

                <Box>
                  {exp.contributions.map((item, idx) => (
                    <AnimatedListItem
                      key={idx}
                      sx={{
                        mb: 2,
                        animationDelay: `${idx * 0.1}s`,
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          background: "rgba(0, 0, 0, 0.02)",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            background: "rgba(0, 0, 0, 0.04)",
                            transform: "translateX(4px)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            alignItems: "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              color: "#536493",
                              mt: 0.5,
                              display: "flex",
                            }}
                          >
                            <ChevronRight size={16} />
                          </Box>
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{
                              color: "text.primary",
                              lineHeight: 1.6,
                              flex: 1,
                            }}
                          >
                            {item}
                          </Typography>
                        </Box>
                      </Paper>
                    </AnimatedListItem>
                  ))}
                </Box>
              </AnimatedContent>
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Experience;
